// AI Copilot widget — thin wrapper around the copilot's loader script.
// Loads /widget.js once, then follows the signed-in HRM session (the `accessToken` cookie).
// The copilot is a separate, already-deployed service: no backend/SDK is installed here — this
// just injects a <script> tag and hands it the token HRM already holds. If the copilot is
// unreachable, every function fails quietly so HRM keeps working without it.
import Cookies from 'js-cookie'

// Trailing slashes are stripped so `${ORIGIN}/widget.js` can never become `//widget.js`.
const ORIGIN = (
  process.env.REACT_APP_COPILOT_ORIGIN || 'https://zealous-sea-08127a800.7.azurestaticapps.net'
).replace(/\/+$/, '')
const APP_CODE = 'hrms'
const SCRIPT_ID = 'ai-copilot-widget'
const ACCENT = '#4f46e5' // paints the bubble in HRM's colour on the very first load
const ENABLED_COOKIE = 'copilotEnabled'
const COPILOT_MODULE_NAME = 'Copilot Chatbot'
const COPILOT_TOGGLE_SETTING_KEY = 'copilot_toggle'

let loading = null

// Reads the "Copilot Chatbot" module's copilot_toggle setting out of the login response's
// feature list and remembers it, so mountCopilot() honours it on every reload too (not just
// right after login). If the module is missing from the response (older backend), leave the
// widget on — that's the behaviour before this toggle existed.
export function setCopilotEnabledFromFeatures(features) {
  const copilotModule = (Array.isArray(features) ? features : []).find(
    (feature) => feature?.name === COPILOT_MODULE_NAME,
  )
  const toggleSetting = Array.isArray(copilotModule?.settings)
    ? copilotModule.settings.find((setting) => setting?.setting_key === COPILOT_TOGGLE_SETTING_KEY)
    : null
  const enabled = copilotModule ? toggleSetting?.is_enabled === true : true
  Cookies.set(ENABLED_COOKIE, String(enabled), { expires: 1 })
}

function isCopilotEnabled() {
  const stored = Cookies.get(ENABLED_COOKIE)
  return stored === undefined ? true : stored === 'true'
}

function loadScript() {
  if (window.AICopilot) return Promise.resolve()
  if (loading) return loading
  loading = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID)
    const el = existing || document.createElement('script')
    if (!existing) {
      el.id = SCRIPT_ID
      el.src = `${ORIGIN}/widget.js`
      el.defer = true
      document.body.appendChild(el)
    }
    el.addEventListener('load', resolve)
    el.addEventListener('error', () => {
      loading = null // allow a retry on the next mountCopilot()
      // Drop the dead tag: a spent <script> never fires load/error again, so leaving
      // it behind would make the next retry wait on a promise that never settles.
      el.remove()
      reject(new Error(`Failed to load AI Copilot widget from ${ORIGIN}/widget.js`))
    })
  })
  return loading
}

// Show the bubble for the signed-in user. Safe to call on every app load and after login.
export function mountCopilot() {
  const token = Cookies.get('accessToken')
  if (!token || !isCopilotEnabled()) return // not signed in, or the module is toggled off
  loadScript()
    .then(() => {
      const t = Cookies.get('accessToken') // re-read: may have changed while the script loaded
      if (t) window.AICopilot.init({ token: t, app: APP_CODE, accent: ACCENT })
    })
    .catch((error) => {
      // copilot unreachable — HRM must keep working without it, but say so in the
      // console: a wrong REACT_APP_COPILOT_ORIGIN is otherwise indistinguishable
      // from the module being toggled off.
      console.warn(`AI Copilot widget not loaded from ${ORIGIN}:`, error?.message || error)
    })
}

// Push a refreshed token so the bubble never goes stale mid-session.
export function updateCopilotToken(token) {
  if (token && window.AICopilot) window.AICopilot.setToken(token)
}

// Remove the bubble on logout (an SPA logout doesn't reload the page).
export function destroyCopilot() {
  if (window.AICopilot) window.AICopilot.destroy()
}
